import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  TextField
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { theme } from "../../../../../theme";
import CancelIcon from '@mui/icons-material/Cancel';
import { Question } from "./HiringExperienceConfiguration";

interface Props {
  index: number;
  question: Question;
  onAnswerChange: (id: number, answer: string) => void;
  handleUpdateCustomQuestion?: (question_num: number, question: string) => void;
  setQuestionNumToDelete?: (num: number | null) => void;
  setDeleteDialogOpen?: (open: boolean) => void;
  displayingForAdmin?: boolean;
}

const QuestionItem: React.FC<Props> = ({
  index,
  question,
  onAnswerChange,
  handleUpdateCustomQuestion,
  setQuestionNumToDelete,
  setDeleteDialogOpen,
  displayingForAdmin = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(question.Question);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!editedText.trim()) {
      setError("Question cannot be empty");
      return;
    }
    if (handleUpdateCustomQuestion) {
      await handleUpdateCustomQuestion(question.QuestionNum, editedText.trim());
    }
    setIsEditing(false);
    setError(null);
  };

  return (
    <Box mt={2}>
      <Box display="flex" alignItems="center" gap={1} mb={1} mr={3}>
        {isEditing ? (
          <>
            <TextField
              size="small"
              fullWidth
              value={editedText}
              onChange={(e) => {
                setEditedText(e.target.value);
                if (e.target.value.trim()) setError(null);
              }}
              error={!!error}
              helperText={error}
            />

            <IconButton
              color="primary"
              title="Save Question"
              onClick={handleSave}
            >
              <SaveIcon />
            </IconButton>

            <IconButton color="primary" title="Cancel Editing" onClick={() => setIsEditing(false)}>
              <CancelIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {index}. {question.Question}
            </Typography>
            {
              displayingForAdmin ? (
                <>
                  <IconButton
                    size="small"
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => setIsEditing(true)}
                    title="Edit Question"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => {
                      if (setQuestionNumToDelete && setDeleteDialogOpen) {
                        setQuestionNumToDelete(question.QuestionNum);
                        setDeleteDialogOpen(true);
                      }
                    }}
                    title="Delete Question"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <></>
              )
            }

          </>
        )}
      </Box>

      <RadioGroup
        value={question.Answer || ""}
        onChange={(e) => onAnswerChange(question.QuestionNum, e.target.value)}
        sx={{ display: 'flex', flexDirection: "row",gap:15 }}
      >
        <FormControlLabel
          value="Excellent"
          control={<Radio />}
          label="Excellent"
          sx={{ '& .MuiTypography-root': { fontWeight: 400 } }}
          disabled={displayingForAdmin}
        />
        <FormControlLabel
          value="Satisfactory"
          control={<Radio />}
          label="Satisfactory"
          sx={{ '& .MuiTypography-root': { fontWeight: 400 } }}
          disabled={displayingForAdmin}
        />
        <FormControlLabel
          value="Can Improve"
          control={<Radio />}
          label="Can Improve"
          sx={{ '& .MuiTypography-root': { fontWeight: 400 } }}
          disabled={displayingForAdmin}
        />
      </RadioGroup>

      <Divider sx={{ mr: 3 }} />
    </Box>
  );
};

export default QuestionItem;
